import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Factory, Boxes, GitMerge, Plug, ArrowRight, Truck, TrendingDown, Route, Gauge, TrendingUp } from "lucide-react";
import { api } from "../../services/api";

const SEV = {
  Critical: { color: "#dc2626", bg: "#fef2f2", ring: "#fecaca" },
  High: { color: "#ea580c", bg: "#fff7ed", ring: "#fed7aa" },
  Medium: { color: "#ca8a04", bg: "#fefce8", ring: "#fde68a" },
  Low: { color: "#0891b2", bg: "#ecfeff", ring: "#a5f3fc" },
};

function Sparkline({ values, color }) {
  const w = 108, h = 34, pad = 2;
  const min = Math.min(...values), max = Math.max(...values);
  const span = max - min || 1;
  const pts = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * (w - pad * 2);
    const y = h - pad - ((v - min) / span) * (h - pad * 2);
    return [x, y];
  });
  const d = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = `${d} L${pts[pts.length - 1][0].toFixed(1)},${h} L${pts[0][0].toFixed(1)},${h} Z`;
  const gid = `sg-${color.replace("#", "")}`;
  return (
    <svg className="spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={d} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="2.4" fill={color} />
    </svg>
  );
}

function fmtVal(v, fmt, unit) {
  if (fmt === "inr") return `₹${v.toLocaleString("en-IN")}${unit === "₹/t" ? "/t" : ""}`;
  if (fmt === "pct") return `${v}%`;
  return `${v}`;
}

function TrendTile({ t }) {
  const color = t.tone === "good" ? "#059669" : "#dc2626";
  const Arrow = t.direction === "up" ? TrendingUp : TrendingDown;
  return (
    <div className={`trend-tile ${t.tone}`} data-testid={`dalmia-trend-${t.key}`}>
      <div className="tt-top">
        <span className="tt-short">{t.short}</span>
        <span className="tt-delta" style={{ color }}><Arrow size={13} /> {t.delta_pct > 0 ? "+" : ""}{t.delta_pct}%</span>
      </div>
      <div className="tt-value">{fmtVal(t.current, t.fmt, t.unit)}</div>
      <Sparkline values={t.values} color={color} />
      <div className="tt-label">{t.label} · 14d{t.floor != null ? ` · floor ${fmtVal(t.floor, t.fmt, t.unit)}` : ""}</div>
    </div>
  );
}

const CAT_ICON = { fulfillment: Truck, margin: TrendingDown, logistics: Route, procurement: Gauge };
const CAT_LABEL = { fulfillment: "Fulfillment Risk", margin: "Margin Early Warning", logistics: "Logistics Bottleneck", procurement: "Input-Cost Signal" };

function Metric({ icon: Icon, label, value, tone, sub }) {
  return (
    <div className="metric" style={tone ? { borderTopColor: tone } : {}}>
      <div className="metric-icon" style={tone ? { background: `${tone}14`, color: tone } : {}}><Icon size={18} /></div>
      <div className="metric-value">{value}</div>
      <div className="metric-label">{label}</div>
      {sub && <div className="metric-sub">{sub}</div>}
    </div>
  );
}

export default function DalmiaDashboard() {
  const [sum, setSum] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [events, setEvents] = useState([]);
  const [trends, setTrends] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.dalmiaSummary().then(setSum);
    api.dalmiaDecisions().then((d) => setDecisions(d.top));
    api.dalmiaEvents().then((d) => setEvents(d.events.slice(0, 8)));
    api.dalmiaTrends().then((d) => setTrends(d.tiles));
  }, []);

  if (!sum) return <div className="loading-page">Loading Dalmia intelligence…</div>;

  return (
    <div className="dash" data-testid="dalmia-dashboard-page">
      <div className="vans-eyebrow" style={{ color: "#0f766e", marginBottom: 4 }}>
        Dalmia Bharat · Intelligent Fulfillment + Margin Early Warning · {sum.today}
      </div>
      <h1 className="vans-title" style={{ marginBottom: 18 }}>Executive Dashboard</h1>

      <div className="metrics-row">
        <Metric icon={AlertTriangle} label="Critical Decisions" value={sum.critical_count} tone={SEV.Critical.color} sub={`${sum.total_decisions} correlated situations`} />
        <Metric icon={Truck} label="Ranked Today" value={sum.top_count} tone="#0d9488" sub="above the corroboration cutoff" />
        <Metric icon={Boxes} label="Entities Flagged" value={sum.entities_flagged} tone="#7c3aed" />
        <Metric icon={Factory} label="Plants Monitored" value={sum.plants_monitored} tone="#4f46e5" />
        <Metric icon={GitMerge} label="Categories" value={Object.values(sum.by_category).filter(Boolean).length} tone="#059669" sub="fulfillment · margin · logistics · input-cost" />
        <Metric icon={Plug} label="Systems Connected" value={sum.systems_connected} tone="#0f172a" />
      </div>

      {trends.length > 0 && (
        <div className="trend-row" data-testid="dalmia-trend-tiles">
          {trends.map((t) => <TrendTile key={t.key} t={t} />)}
        </div>
      )}

      <div className="dash-cols dalmia-dash-cols">
        <div className="sev-bar-card">
          <div className="card-h">Severity distribution</div>
          <div className="sev-bar">
            {["Critical", "High", "Medium", "Low"].map((s) => {
              const n = sum.by_severity[s] || 0;
              return n ? (
                <div key={s} className="sev-seg" style={{ flex: n, background: SEV[s].color }} title={`${s}: ${n}`}>{n}</div>
              ) : null;
            })}
          </div>
          <div className="sev-legend">
            {["Critical", "High", "Medium", "Low"].map((s) => (
              <span key={s} className="sev-leg"><i style={{ background: SEV[s].color }} />{s} · {sum.by_severity[s] || 0}</span>
            ))}
          </div>
        </div>

        <div className="sev-bar-card">
          <div className="card-h">Decisions by category</div>
          <div className="cat-breakdown">
            {Object.entries(sum.by_category).map(([c, n]) => {
              const Icon = CAT_ICON[c] || Truck;
              return (
                <button key={c} className="cat-row" onClick={() => nav("/dalmia/insights")} data-testid={`dalmia-cat-${c}`}>
                  <span className="cat-row-ic"><Icon size={15} /></span>
                  <span className="cat-row-lbl">{CAT_LABEL[c] || c}</span>
                  <span className="cat-row-n">{n}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="dash-cols">
        <section className="panel">
          <div className="panel-h">
            <span>Needs Attention</span>
            <button className="link-btn" onClick={() => nav("/dalmia/insights")} data-testid="dalmia-view-all">View all <ArrowRight size={14} /></button>
          </div>
          <div className="attn-list">
            {decisions.map((d) => (
              <button key={d.id} className="attn-item" onClick={() => nav(`/dalmia/insights?open=${d.id}`)} data-testid={`dalmia-attn-${d.id}`}>
                <span className="sev-chip" style={{ background: SEV[d.severity].bg, color: SEV[d.severity].color, borderColor: SEV[d.severity].ring }}>{d.severity}</span>
                <div className="attn-main">
                  <div className="attn-title">{d.title}</div>
                  <div className="attn-meta">{d.entity_name} · {d.signal_count} signals · {d.source_count} systems · conf {d.confidence}%</div>
                </div>
                <div className="attn-exp">score {d.score}</div>
              </button>
            ))}
          </div>
        </section>

        <section className="panel">
          <div className="panel-h"><span>What Changed</span></div>
          <div className="change-list">
            {events.map((e) => (
              <div key={e.id} className="change-item">
                <span className={`kind-dot k-${e.kind}`} />
                <div>
                  <div className="change-what">{e.what}</div>
                  <div className="change-src">{e.source} · {e.record_id} · {e.time}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
